import { useState, type KeyboardEvent } from 'react'
import { motion } from 'framer-motion'
import { Send, CheckCircle2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

interface TicketFormProps {
  /** Pergunta original que não foi respondida */
  originalQuestion?: string
  onSubmitted?: () => void
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error'

export function TicketForm({ originalQuestion, onSubmitted }: TicketFormProps) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState(originalQuestion ?? '')
  const [status, setStatus] = useState<FormStatus>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const isValid = name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && message.trim()

  const handleSubmit = async () => {
    if (!isValid || status === 'submitting') return

    setStatus('submitting')
    setErrorMsg('')

    try {
      const res = await fetch('/api/support/tickets/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          originalQuestion: originalQuestion || null,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Erro ao enviar')
      }

      setStatus('success')
      onSubmitted?.()
    } catch (err) {
      setStatus('error')
      setErrorMsg(
        err instanceof Error && err.message !== 'Erro ao enviar'
          ? err.message
          : 'Não foi possível enviar seu ticket. Tente novamente.',
      )
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && e.currentTarget.tagName !== 'TEXTAREA') {
      e.preventDefault()
      handleSubmit()
    }
  }

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center"
      >
        <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-emerald-500/15 flex items-center justify-center">
          <CheckCircle2 className="h-6 w-6 text-emerald-500" />
        </div>
        <h3 className="text-base font-semibold text-foreground mb-1">
          Ticket enviado com sucesso!
        </h3>
        <p className="text-sm text-muted-foreground">
          Nossa equipe vai analisar sua dúvida e entrar em contato pelo e-mail informado.
        </p>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-border bg-card p-6 space-y-4"
    >
      <div className="flex items-start gap-3 mb-2">
        <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
          <AlertCircle className="h-5 w-5 text-amber-500" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Não encontramos a resposta
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Preencha o formulário abaixo e nossa equipe entrará em contato.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label htmlFor="ticket-name" className="text-xs font-medium text-muted-foreground mb-1 block">
            Nome
          </label>
          <Input
            id="ticket-name"
            placeholder="Seu nome completo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-10"
            disabled={status === 'submitting'}
          />
        </div>
        <div>
          <label htmlFor="ticket-email" className="text-xs font-medium text-muted-foreground mb-1 block">
            E-mail
          </label>
          <Input
            id="ticket-email"
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-10"
            disabled={status === 'submitting'}
          />
        </div>
        <div>
          <label htmlFor="ticket-message" className="text-xs font-medium text-muted-foreground mb-1 block">
            Descreva sua dúvida
          </label>
          <Textarea
            id="ticket-message"
            placeholder="Descreva sua dúvida com detalhes..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="min-h-[80px] resize-none"
            disabled={status === 'submitting'}
          />
        </div>
      </div>

      {errorMsg && (
        <p className="text-xs text-destructive">{errorMsg}</p>
      )}

      <Button
        onClick={handleSubmit}
        disabled={!isValid || status === 'submitting'}
        className="w-full h-10"
      >
        {status === 'submitting' ? (
          <span className="flex items-center gap-2">
            <span className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Enviando...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Enviar Ticket
          </span>
        )}
      </Button>
    </motion.div>
  )
}
